import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import type {TransformAccessor} from "#template/interactions"
import {Matrix4} from "@cm/math"

const TD = TypeDescriptors

export class TransformState {
    matrix: Matrix4 | undefined = undefined
    controlledBySolver = false
    solverNeedsUpdate = true
    isFirstUpdate = true
    isInteracting = true
}

const transformDescriptor = {
    state: TD.inlet(TD.Identity<TransformState>()),
    parentMatrix: TD.inlet(TD.Matrix4),
    lockedTransform: TD.inlet(TD.Nullable(TD.Matrix4)),
    defaultTransform: TD.inlet(TD.Nullable(TD.Matrix4)),
    matrix: TD.outlet(TD.Matrix4),
    accessor: TD.outlet(TD.Identity<TransformAccessor>()),
    isInteracting: TD.outlet(TD.Boolean),
}

export class Transform implements TransformAccessor, NodeClassImpl<typeof transformDescriptor, typeof Transform> {
    static descriptor = transformDescriptor
    static uniqueName = "Transform"
    state!: Inlet<TransformState>
    parentMatrix!: Inlet<Matrix4>
    lockedTransform!: Inlet<Matrix4 | null>
    defaultTransform!: Inlet<Matrix4 | null>
    matrix!: Outlet<Matrix4>
    accessor!: Outlet<TransformAccessor>
    isInteracting!: Outlet<boolean>

    run() {
        if (this.state === NotReady || this.parentMatrix === NotReady || this.lockedTransform === NotReady || this.defaultTransform === NotReady) {
            this.accessor.emitIfChanged(NotReady)
            this.matrix.emitIfChanged(NotReady)
            this.isInteracting.emitIfChanged(false)
            return
        }
        this.isInteracting.emitIfChanged(this.state.isInteracting)
        if (this.lockedTransform && this.parentMatrix) {
            this.state.controlledBySolver = false
            this.state.solverNeedsUpdate = true
            this.state.matrix = this.parentMatrix.multiply(this.lockedTransform)
        } else {
            this.state.controlledBySolver = true
            if (this.state.isFirstUpdate) {
                if (this.defaultTransform) {
                    this.state.matrix = this.defaultTransform.copy()
                } else {
                    this.state.matrix = Matrix4.fromArray([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1])
                }

                this.state.isFirstUpdate = false
                this.state.solverNeedsUpdate = true
            }
        }
        this.accessor.emitIfChanged(this)
        if (this.state.matrix) this.matrix.emitIfChanged(this.state.matrix)
    }

    // TransformAccessor interface:

    getTransform() {
        if (this.state === NotReady) return undefined
        return this.state.matrix
    }

    setTransform(matrix: Matrix4, interactive?: boolean) {
        if (this.state === NotReady) return
        this.state.isInteracting = !!interactive
        this.isInteracting.emitIfChanged(this.state.isInteracting)
        if (interactive) {
            this.state.solverNeedsUpdate = true
        }
        if (!this.state.matrix || !this.state.matrix.equals(matrix)) {
            this.state.matrix = matrix
            this.matrix.emitIfChanged(matrix)
        }
    }

    endTransform() {
        if (this.state === NotReady) return
        this.state.isInteracting = false
        this.isInteracting.emitIfChanged(false)
        //TODO: end interaction in solver
    }

    get controlledBySolver(): boolean {
        if (this.state === NotReady) return false
        return this.state.controlledBySolver
    }

    checkSolverNeedsUpdate(): boolean {
        if (this.state === NotReady) return false
        if (this.state.solverNeedsUpdate) {
            this.state.solverNeedsUpdate = false
            return true
        } else {
            return false
        }
    }
}
